import ReactECharts from 'echarts-for-react';

/**
 * ProcessGraphChart_2 — "minimal editorial" variant of ProcessGraphChart (blog06).
 * Same directed process graph with an approval gate, same nodes/edges/positions/
 * sizes — zero invented data. Opaque white card, generous whitespace, thin
 * strokes, muted slate palette with a single teal accent. Quiet
 * magazine-infographic feel — no gradients, glows, or shadows.
 */

const PALETTE = {
  accent: '#0AB3BF', // single teal accent — bot steps and the decisive edge
  slate1: '#64748B', // muted slate
  slate2: '#94A3B8', // lighter muted slate
  slate3: '#CBD5E1', // lightest slate, still readable on white
  text: '#64748B',
  textSoft: '#94A3B8',
  hairline: '#E2E8F0',
  surface: '#FFFFFF',
};

const CATEGORIES = [
  { name: 'Step', itemStyle: { color: PALETTE.accent } },
  { name: 'Gate', itemStyle: { color: PALETTE.slate1 } },
  { name: 'External', itemStyle: { color: PALETTE.slate2 } },
  { name: 'Log', itemStyle: { color: PALETTE.slate3 } },
];

const NODES = [
  { name: 'Qualify lead', category: 0, x: 40, y: 40, symbolSize: 64 },
  { name: 'Draft outreach', category: 0, x: 40, y: 150, symbolSize: 64 },
  { name: 'Human review gate', category: 1, x: 40, y: 260, symbolSize: 76 },
  { name: 'Send email', category: 2, x: 40, y: 370, symbolSize: 68 },
  { name: 'Log the run', category: 3, x: 40, y: 480, symbolSize: 64 },
];

const EDGES = [
  { source: 'Qualify lead', target: 'Draft outreach' },
  { source: 'Draft outreach', target: 'Human review gate' },
  { source: 'Human review gate', target: 'Send email', lineStyle: { color: PALETTE.accent } },
  { source: 'Send email', target: 'Log the run', lineStyle: { color: PALETTE.slate2 } },
];

/** ONE shared name formatter — tooltip, node labels, and legend all use it. */
const formatName = (name: string) => name;

const LEGEND_LABELS: Record<string, string> = {
  Step: 'Step (bot)',
  Gate: 'Approval gate',
  External: 'External action',
  Log: 'Log row',
};

export const ProcessGraphChart_2 = ({ height = 440 }: { height?: number }) => {
  const option = {
    backgroundColor: PALETTE.surface,
    tooltip: {
      backgroundColor: PALETTE.surface,
      borderColor: PALETTE.hairline,
      borderWidth: 1,
      textStyle: { color: PALETTE.text, fontSize: 12 },
      formatter: (p: { name: string }) => formatName(p.name),
    },
    legend: {
      top: 8,
      icon: 'circle',
      itemWidth: 9,
      itemHeight: 9,
      textStyle: { color: PALETTE.text, fontSize: 11 },
      formatter: (name: string) => LEGEND_LABELS[formatName(name)] ?? formatName(name),
    },
    series: [
      {
        type: 'graph',
        layout: 'none',
        roam: true,
        draggable: true,
        data: NODES,
        links: EDGES,
        categories: CATEGORIES,
        symbol: 'circle',
        label: {
          show: true,
          position: 'bottom',
          color: PALETTE.text,
          fontSize: 11,
          fontWeight: 600,
          formatter: (p: { name: string }) => formatName(p.name),
        },
        lineStyle: { color: PALETTE.slate2, width: 1, curveness: 0.08 },
        edgeSymbol: ['none', 'arrow'],
        edgeSymbolSize: 8,
        emphasis: { focus: 'adjacency', lineStyle: { width: 2 } },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white p-4">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 text-center">
        Figure 1. A process is a directed graph: bot steps feed a human review gate, and only an approved action crosses
        to the outside world — then the whole run is logged. Drag to reposition; the edge to the gate is the decision.
      </p>
    </div>
  );
};
